import { supabase } from "@/lib/supabase";
import type { EventFeedRow } from "@/lib/api/participants";

/**
 * Organizer recommendations (TZ block C). Each row represents one
 * deliberate "drop candidate A into recipient B's feed" decision the
 * organizer made. The recipient sees these prepended to their feed
 * with a "Recommended by organizer" badge; the organizer sees the
 * list of who they've already recommended next to each participant.
 */

export interface RecommendedRow extends EventFeedRow {
  recommended_at: string;
}

/**
 * Create (or no-op if exists) a recommendation. Returns the rec id.
 * Caller must be the event organizer — server enforces via auth.uid().
 */
export async function recommendParticipantToFeed(
  recipientParticipantId: string,
  candidateParticipantId: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("recommend_participant_to_feed", {
    p_recipient_participant_id: recipientParticipantId,
    p_candidate_participant_id: candidateParticipantId,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function unrecommendParticipantFromFeed(
  recipientParticipantId: string,
  candidateParticipantId: string,
): Promise<void> {
  const { error } = await supabase.rpc("unrecommend_participant_from_feed", {
    p_recipient_participant_id: recipientParticipantId,
    p_candidate_participant_id: candidateParticipantId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Fetch the set of recommendations dropped into this participant's
 * feed by the organizer. Returns full EventFeedRow-shaped rows so the
 * FE can render them with the same FeedCard component.
 */
export async function fetchFeedRecommendations(
  participantId: string,
): Promise<RecommendedRow[]> {
  const { data, error } = await supabase.rpc(
    "find_event_feed_recommendations",
    { p_participant_id: participantId },
  );
  if (error) throw new Error(error.message);
  return (data ?? []) as RecommendedRow[];
}

/**
 * Organizer-side: list every (recipient, candidate) pair the organizer
 * has set up for this event. Used to (a) avoid showing the Recommend
 * button when a pair already exists and (b) render a small "Already
 * recommended to N" badge next to each participant in the dashboard.
 */
export interface OrganizerRecommendationRow {
  id: string;
  event_id: string;
  recipient_participant_id: string;
  candidate_participant_id: string;
  created_at: string;
}

export async function fetchEventRecommendations(
  eventId: string,
): Promise<OrganizerRecommendationRow[]> {
  const { data, error } = await supabase
    .from("event_feed_recommendations")
    .select("id, event_id, recipient_participant_id, candidate_participant_id, created_at")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as OrganizerRecommendationRow[];
}

/**
 * V3 — single-click bidirectional recommendation. Inserts both
 * directions atomically; enforces the 5-active-recipient limit on
 * both sides server-side.
 */
export async function recommendPairBidirectional(
  participantAId: string,
  participantBId: string,
): Promise<void> {
  const { error } = await supabase.rpc("recommend_pair_bidirectional", {
    p_a: participantAId,
    p_b: participantBId,
  });
  if (error) throw new Error(error.message);
}

export async function unrecommendPairBidirectional(
  participantAId: string,
  participantBId: string,
): Promise<void> {
  const { error } = await supabase.rpc("unrecommend_pair_bidirectional", {
    p_a: participantAId,
    p_b: participantBId,
  });
  if (error) throw new Error(error.message);
}

/**
 * V3-10 — organizer-driven 'finish your profile' DM/email. Fire-and-
 * forget; the modal toasts on click but doesn't block.
 */
export async function sendCompleteProfileInvite(
  participantId: string,
): Promise<{ ok: boolean; sent?: boolean; channel?: string; reason?: string }> {
  const { env } = await import("@/lib/env");
  const session = await supabase.auth.getSession();
  const jwt = session.data.session?.access_token ?? "";
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/send-meetup-complete-profile-invite`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: jwt ? `Bearer ${jwt}` : `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ participant_id: participantId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as {
    ok: boolean;
    sent?: boolean;
    channel?: string;
    reason?: string;
  };
}

/**
 * V3-8 — optional TG nudge to both sides of a bidirectional rec.
 * Fire-and-forget from the modal when the organizer flipped the
 * Notify switch. Errors are swallowed so a TG misconfig never blocks
 * the rec itself.
 */
export function fireOrganizerRecPush(
  eventId: string,
  participantAId: string,
  participantBId: string,
): void {
  import("@/lib/env").then(({ env }) => {
    import("@/lib/supabase").then(({ supabase }) => {
      void supabase.auth.getSession().then((res) => {
        const jwt = res.data.session?.access_token ?? "";
        const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/send-meetup-organizer-rec-push`;
        fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: env.VITE_SUPABASE_ANON_KEY,
            Authorization: jwt
              ? `Bearer ${jwt}`
              : `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            event_id: eventId,
            participant_a_id: participantAId,
            participant_b_id: participantBId,
          }),
        }).catch(() => {
          /* swallow */
        });
      });
    });
  });
}

/**
 * Candidate row returned by find_recommend_candidates — the recipient
 * is implicit (the panel target). Carries direct + reverse score
 * plus state flags so the panel can render every variant from the
 * Frame 03 mockup without follow-up queries.
 */
export interface RecommendCandidateRow {
  id: string;
  name: string;
  role: string | null;
  company: string | null;
  video_storage_path: string | null;
  pitch_status: string;
  profile_json: Record<string, unknown> | null;
  looking_for: string | null;
  can_help_with: string | null;
  has_video: boolean;
  is_profile_complete: boolean;
  /** Candidate's offer → recipient's need score, 1..100, null when unscored. */
  direct_score: number | null;
  /** Recipient's offer → candidate's need score, 1..100, null when unscored. */
  reverse_score: number | null;
  /** min(direct, reverse) with nulls treated as 0 — drives the sort. */
  mutual_strength: number;
  direct_reason: string | null;
  reverse_reason: string | null;
  already_recommended: boolean;
  recommended_at: string | null;
  already_matched: boolean;
}

export async function fetchRecommendCandidates(
  recipientParticipantId: string,
): Promise<RecommendCandidateRow[]> {
  const { data, error } = await supabase.rpc("find_recommend_candidates", {
    p_recipient_participant_id: recipientParticipantId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as RecommendCandidateRow[];
}

/**
 * V3-11 — pair returned by the auto-suggest queue. Each row already
 * carries names + roles for both sides so the queue UI doesn't have
 * to do per-pair lookups.
 */
export interface AutoSuggestPairRow {
  a_id: string;
  a_name: string;
  a_role: string | null;
  a_company: string | null;
  a_has_video: boolean;
  b_id: string;
  b_name: string;
  b_role: string | null;
  b_company: string | null;
  b_has_video: boolean;
  direct_score: number | null;
  reverse_score: number | null;
  mutual_strength: number;
  direct_reason: string | null;
  reverse_reason: string | null;
}

export async function fetchAutoSuggestPairs(
  eventId: string,
  limit = 50,
): Promise<AutoSuggestPairRow[]> {
  const { data, error } = await supabase.rpc("find_event_auto_suggest_pairs", {
    p_event_id: eventId,
    p_limit: limit,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as AutoSuggestPairRow[];
}

/**
 * The ⚡ N potential badge value for a participant in the
 * Participants tab — count of recommendable candidates this
 * participant could be paired with.
 */
export async function fetchParticipantPotentialCount(
  participantId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc(
    "find_participant_potential_count",
    { p_participant_id: participantId },
  );
  if (error) throw new Error(error.message);
  return typeof data === "number" ? data : 0;
}
